// LOAD-BEARING SIDE-EFFECT IMPORT — do not delete as "unused". See the
// `src/hooks/query/integrations/useQueryEhrLaunchPastNotes.ts` delegate on this
// door for the measurement.
import '@/pages/api/scribeAPIV2';

import { useBuildSessionPath } from '@/features/sessions/utils/buildSessionPath';
import { invalidateIntegrationsMixinGetScheduleQuery } from '@/hooks/integrations/integrations-framework/mixins/useQueryIntegrationsMixinGetSchedule';
import { invalidateSessionsQuery } from '@/hooks/query/useQueryCombinedSessions';
import { getIntegrationLinkedSessionInfo } from '@/pages/api/scribeAPIV2';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import {
  type CreateIntegrationSessionWorkflowResponse,
  POST_INTEGRATION_WORKFLOW_SESSION_MUTATION_KEY,
} from '@heidi/integrations/ehr/contracts';
import {
  hydrateIntegrationPatientDemographics,
  useMutatePostIntegrationWorkflowSession as useMutatePostIntegrationWorkflowSessionCore,
} from '@heidi/integrations/ehr/data';
import { GET_PATIENT_PROFILES } from '@heidi/patients/contracts';
import { logger as runtimeLogger, sanitizeError } from '@heidi/runtime/logger';
import { invalidateSessionById } from '@heidi/session/data';
import { useMutationState } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useRouter } from 'next/router';

// Re-exported so the `@heidi/integrations/ehr/contracts` head is reachable from
// this path too. It was module-private pre-carve, so nothing is owed it — the
// re-export exists because this file itself reads it, and stating that once is
// cheaper than two importers of the same head disagreeing about where it lives.
export { POST_INTEGRATION_WORKFLOW_SESSION_MUTATION_KEY };

/**
 * ONE OF THE TWO IDENTITIES THIS FILE DECLARED STAYS HERE, and it is a live
 * defect rather than a carve decision. `GET_INTEGRATION_LINKED_SESSION_INFO` is
 * an ad-hoc `fetchQuery` head minted inside the 409 recovery below, and it reads
 * `'GetIntegrationLinkedSessionInfo'` while `useQueryIntegrationLinkSessionInfo`
 * writes `'GetIntegrationLinkSessionInfo'` — LinkED versus Link, one letter — so
 * the recovery reads a key nothing has ever populated and re-requests every
 * time. That is already recorded against the pod that owns this hook. Moving the
 * head into `/contracts` would freeze the defect behind a door; fixing it here
 * would ship a behaviour change under a refactor's title. It stays, unchanged,
 * where the fix will find it.
 */
const GET_INTEGRATION_LINKED_SESSION_INFO = 'GetIntegrationLinkedSessionInfo';

/**
 * Second half of creating a session from a Veradigm schedule row.
 *
 * The schedule no longer carries date of birth or sex (one vendor read per
 * patient made the schedule slow), so the session is created against a profile
 * that holds only the patient's name and vendor ID. The backend flags that with
 * `patient_demographics_pending`; this asks it to make the one vendor read now,
 * off the session-create path, and then refreshes every cache that renders the
 * profile so the header's DOB / sex / "Jane Smith, F, 45" line fills in.
 *
 * Fire-and-forget by design. The caller has already navigated; a failed read
 * leaves the profile with the name only, and the backend guards mean nothing
 * stored is overwritten. Never awaited by the caller, so it must not throw.
 *
 * Module-private on purpose: it is app orchestration around a door api fn, the
 * same tier as the 409 recovery below, and stays with the delegate that owns
 * the sequence rather than becoming a new legacy hub symbol.
 */
async function hydratePatientDemographicsAfterSessionCreate(
  response: CreateIntegrationSessionWorkflowResponse | undefined
): Promise<void> {
  const patientProfileId = response?.patient_profile_id;
  if (!response?.patient_demographics_pending || !patientProfileId) {
    return;
  }
  const sessionId = response.session?.session_id;

  try {
    const { data } = await hydrateIntegrationPatientDemographics({ patientProfileId });
    if (!data?.hydrated) {
      return;
    }
    if (sessionId) {
      invalidateSessionById(sessionId);
    }
    void reactQueryClient.invalidateQueries({ queryKey: [GET_PATIENT_PROFILES] });
    void invalidateSessionsQuery();
  } catch (error) {
    runtimeLogger.warn(
      '[integrations] patient demographics hydration failed after session create',
      sanitizeError(error)
    );
  }
}

export const useMutateStatePostIntegrationWorkflowSession = () => {
  const { mutate, mutateAsync } = useMutatePostIntegrationWorkflowSession();

  const mutationState = useMutationState({
    filters: { mutationKey: [POST_INTEGRATION_WORKFLOW_SESSION_MUTATION_KEY] },
    select: (mutation) => mutation.state,
  });
  const currentMutation = mutationState.at(-1);

  const isPending = currentMutation?.status === 'pending';
  const isError = currentMutation?.status === 'error';
  const isSuccess = currentMutation?.status === 'success';

  return {
    data: currentMutation?.data,
    isPending,
    isError,
    isSuccess,
    mutate,
    mutateAsync,
  };
};

/**
 * Delegate, tier T2. Only the request and the mutation identity are behind the
 * door; every statement around them is app orchestration and stays here.
 *
 * NO NAVIGATE PORT, WHICH IS THE POINT. `next/router` is banned in the package
 * zones, so a navigate callback is the obvious crossing — and it is unnecessary
 * here, because the router is ONE OF FIVE app dependencies this sequence needs.
 * The other four are `invalidateSessionsQuery`,
 * `invalidateIntegrationsMixinGetScheduleQuery`, the app's own `reactQueryClient`
 * singleton and `useBuildSessionPath` — count them in the import list above. So
 * the whole sequence stays at the composition point that already owns all five;
 * a navigate port would have crossed one of them and left four behind.
 *
 * BOTH PORTS ARE AWAITED, as both handlers were pre-carve: `onSuccess` awaited
 * the sessions invalidation and the push, and `onError`'s 409 recovery awaits a
 * `fetchQuery`, a second invalidation and a second push.
 */
const useMutatePostIntegrationWorkflowSession = () => {
  const router = useRouter();
  const buildSessionPath = useBuildSessionPath();

  return useMutatePostIntegrationWorkflowSessionCore({
    onWorkflowSessionCreated: async ({ data }) => {
      await invalidateSessionsQuery();
      invalidateIntegrationsMixinGetScheduleQuery();
      const newSessionId = data?.session?.session_id;
      if (newSessionId) {
        await router.push(buildSessionPath(newSessionId));
      }
      // Not awaited: the Veradigm patient read this triggers is the round trip
      // the session-create path no longer waits on. It refreshes the caches
      // itself once it lands.
      void hydratePatientDemographicsAfterSessionCreate(data);
    },
    onCreateWorkflowSessionError: async (error, variables) => {
      if (error instanceof AxiosError && error.response?.status === 409) {
        const appointmentId =
          variables.appointment?.provider_details?.unique_identifier?.toString();
        if (appointmentId) {
          try {
            const linkedSession = await reactQueryClient.fetchQuery({
              queryKey: [
                GET_INTEGRATION_LINKED_SESSION_INFO,
                { provider_unique_identifier: appointmentId },
              ],
              queryFn: async () => {
                const { data } = await getIntegrationLinkedSessionInfo({
                  provider_unique_identifier: appointmentId,
                });
                return data;
              },
            });
            if (linkedSession?.session_id) {
              await invalidateSessionsQuery();
              invalidateIntegrationsMixinGetScheduleQuery();
              await router.push(buildSessionPath(linkedSession.session_id));
              return;
            }
          } catch (lookupError) {
            logger.error('Failed to look up existing session:', lookupError);
          }
        }
      }
      logger.error('Error creating integration workflow session:', error);
    },
  });
};
